/**
 * Bootstrap Icons shim with a Lucide-compatible API.
 *
 * Why this exists: we removed `lucide-react` so the app ships only one icon
 * font (Bootstrap Icons, already bundled via `src/styles/index.scss`). All
 * existing JSX kept its names (`<Search />`, `<X />`, …) — imports were just
 * pointed at this module, so call sites compile without changes.
 *
 * Each icon renders an `<i className="bi bi-…" />` and accepts the props
 * Lucide accepted (size/color/strokeWidth/className/standard HTML props +
 * ref). Bootstrap glyphs are font-based; `size` maps to `font-size`.
 *
 * `strokeWidth` / `absoluteStrokeWidth` from Lucide have no Bootstrap Icons
 * analogue and are silently ignored.
 *
 * If you need a glyph not exported here, add it (find the closest match in
 * https://icons.getbootstrap.com/) — DO NOT reintroduce `lucide-react`.
 */
import * as React from 'react';

export interface LucideProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'children'> {
  size?: number | string;
  color?: string;
  /** Lucide-only; ignored by Bootstrap Icons (font glyphs have no stroke) */
  strokeWidth?: number | string;
  /** Lucide-only; ignored */
  absoluteStrokeWidth?: boolean;
}

export type LucideIcon = React.ForwardRefExoticComponent<
  LucideProps & React.RefAttributes<HTMLElement>
>;

const createIcon = (biClass: string, displayName: string): LucideIcon => {
  const Component = React.forwardRef<HTMLElement, LucideProps>(
    (
      {
        className,
        size,
        color,
        strokeWidth: _sw,
        absoluteStrokeWidth: _asw,
        style,
        ...rest
      },
      ref
    ) => {
      const dim = typeof size === 'number' ? `${size}px` : size;
      const mergedStyle: React.CSSProperties = {
        ...(style ?? {}),
        ...(dim ? { fontSize: dim, lineHeight: 1 } : {}),
        ...(color ? { color } : {}),
        display: (style as React.CSSProperties | undefined)?.display ?? 'inline-flex',
        alignItems: (style as React.CSSProperties | undefined)?.alignItems ?? 'center',
        justifyContent:
          (style as React.CSSProperties | undefined)?.justifyContent ?? 'center',
      };
      const finalClass = `bi ${biClass}${className ? ` ${className}` : ''}`;
      return (
        <i
          ref={ref}
          className={finalClass}
          style={mergedStyle}
          aria-hidden="true"
          {...rest}
        />
      );
    }
  );
  Component.displayName = displayName;
  return Component;
};

// ─── Mapped icons (alphabetical) ──────────────────────────────────────────
export const AlertCircle = createIcon('bi-exclamation-circle', 'AlertCircle');
export const AlertTriangle = createIcon(
  'bi-exclamation-triangle',
  'AlertTriangle'
);
export const ArrowLeft = createIcon('bi-arrow-left', 'ArrowLeft');
export const ArrowRight = createIcon('bi-arrow-right', 'ArrowRight');
export const ArrowRightIcon = ArrowRight;
export const Bell = createIcon('bi-bell', 'Bell');
export const BookCopy = createIcon('bi-journal-bookmark', 'BookCopy');
export const Calendar = createIcon('bi-calendar', 'Calendar');
export const CalendarIcon = Calendar;
export const Camera = createIcon('bi-camera', 'Camera');
export const Check = createIcon('bi-check', 'Check');
export const CheckCircle2 = createIcon('bi-check-circle-fill', 'CheckCircle2');
export const ChevronDown = createIcon('bi-chevron-down', 'ChevronDown');
export const ChevronDownIcon = ChevronDown;
export const ChevronLeft = createIcon('bi-chevron-left', 'ChevronLeft');
export const ChevronLeftIcon = ChevronLeft;
export const ChevronRight = createIcon('bi-chevron-right', 'ChevronRight');
export const ChevronRightIcon = ChevronRight;
export const ClipboardCheck = createIcon('bi-clipboard-check', 'ClipboardCheck');
// Bootstrap has no clipboard+clock combo; clipboard-check is the closest
// single-glyph stand-in.
export const ClipboardClock = createIcon('bi-clipboard-check', 'ClipboardClock');
export const ClipboardList = createIcon('bi-clipboard', 'ClipboardList');
export const Clock = createIcon('bi-clock', 'Clock');
export const Edit = createIcon('bi-pencil', 'Edit');
export const Pencil = Edit;
export const Eye = createIcon('bi-eye', 'Eye');
export const FileText = createIcon('bi-file-text', 'FileText');
export const GripVertical = createIcon('bi-grip-vertical', 'GripVertical');
export const Info = createIcon('bi-info-circle', 'Info');
export const LayoutGrid = createIcon('bi-grid', 'LayoutGrid');
export const List = createIcon('bi-list', 'List');
// Spinner: pair with `animate-spin` (already used at call sites) or any custom
// rotation class. The arrow-repeat glyph spins cleanly.
export const Loader2 = createIcon('bi-arrow-repeat', 'Loader2');
export const Mail = createIcon('bi-envelope', 'Mail');
export const MapPin = createIcon('bi-geo-alt', 'MapPin');
export const MessageCircle = createIcon('bi-chat-dots', 'MessageCircle');
export const MessageSquare = createIcon('bi-chat-square', 'MessageSquare');
export const MoreVertical = createIcon('bi-three-dots-vertical', 'MoreVertical');
export const Paperclip = createIcon('bi-paperclip', 'Paperclip');
export const Plus = createIcon('bi-plus', 'Plus');
export const Printer = createIcon('bi-printer', 'Printer');
export const QrCode = createIcon('bi-qr-code', 'QrCode');
export const Radio = createIcon('bi-broadcast', 'Radio');
export const Repeat = createIcon('bi-arrow-repeat', 'Repeat');
export const Save = createIcon('bi-floppy', 'Save');
export const Search = createIcon('bi-search', 'Search');
export const Send = createIcon('bi-send', 'Send');
export const Settings = createIcon('bi-gear', 'Settings');
export const Smartphone = createIcon('bi-phone', 'Smartphone');
export const Trash2 = createIcon('bi-trash', 'Trash2');
export const User = createIcon('bi-person', 'User');
export const Users = createIcon('bi-people', 'Users');
export const X = createIcon('bi-x', 'X');
